import { createTheme } from '@mui/material/styles'

/**
 * Borku admin design tokens.
 *
 * The palette keys below (main2, main3, lightest, darkest, background.primary…)
 * are non-standard MUI slots that views across the app already reference, so
 * they are kept and re-tuned rather than removed — this is a visual refresh,
 * not an API change.
 */
const brand = {
  orange: '#F4661F',
  orangeHover: '#DC5713',
  orangeDeep: '#C24C11',
  orangeTint: '#FFF1E9',
  orangeTintStrong: '#FFE1CE'
}

const neutral = {
  ink: '#111827',
  body: '#334155',
  muted: '#6B7280',
  faint: '#94A3B8',
  line: '#E9EBF0',
  lineSoft: '#F1F3F7',
  surface: '#FFFFFF',
  wash: '#F6F7F9',
  washDeep: '#EEF1F5'
}

const shadow = {
  xs: '0 1px 2px rgba(16, 24, 40, 0.04)',
  sm: '0 1px 2px rgba(16, 24, 40, 0.04), 0 1px 3px rgba(16, 24, 40, 0.06)',
  md: '0 4px 12px rgba(16, 24, 40, 0.06), 0 1px 3px rgba(16, 24, 40, 0.04)',
  lg: '0 12px 32px rgba(16, 24, 40, 0.10)'
}

const theme = createTheme({
  shape: {
    borderRadius: 10
  },
  typography: {
    fontFamily: ['Inter', 'Open Sans', 'sans-serif'].join(),
    htmlFontSize: 16,
    fontSize: 14,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    h1: {
      fontWeight: 700,
      fontSize: '2.125rem',
      lineHeight: 1.2,
      letterSpacing: '-0.02em'
    },
    h2: {
      fontWeight: 700,
      fontSize: '1.75rem',
      lineHeight: 1.25,
      letterSpacing: '-0.02em'
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.375rem',
      lineHeight: 1.3,
      letterSpacing: '-0.01em'
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.35,
      letterSpacing: '-0.01em'
    },
    h5: {
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.4,
      letterSpacing: '0em'
    },
    h6: {
      fontWeight: 600,
      fontSize: '0.875rem',
      lineHeight: 1.45,
      letterSpacing: '0em'
    },
    subtitle1: {
      fontWeight: 600,
      fontSize: '0.9375rem',
      lineHeight: 1.5,
      letterSpacing: '0em'
    },
    subtitle2: {
      fontWeight: 500,
      fontSize: '0.8125rem',
      lineHeight: 1.5,
      letterSpacing: '0em'
    },
    body1: {
      fontWeight: 400,
      fontSize: '0.875rem',
      lineHeight: 1.55,
      letterSpacing: '0em'
    },
    body2: {
      fontWeight: 400,
      fontSize: '0.8125rem',
      lineHeight: 1.55,
      letterSpacing: '0em'
    },
    button: {
      fontWeight: 600,
      fontSize: '0.875rem',
      lineHeight: 1.5,
      letterSpacing: '0em',
      textTransform: 'none'
    },
    caption: {
      fontWeight: 400,
      fontSize: '0.75rem',
      lineHeight: 1.5,
      letterSpacing: '0em'
    },
    overline: {
      fontWeight: 600,
      fontSize: '0.6875rem',
      lineHeight: 1.5,
      letterSpacing: '0.08em',
      textTransform: 'uppercase'
    }
  },
  palette: {
    mode: 'light',
    common: {
      black: neutral.ink,
      blackShade: 'rgba(16, 24, 40, 0.06)',
      white: '#fff'
    },
    primary: {
      main: brand.orange,
      main2: brand.orange,
      main3: brand.orangeHover,
      light: brand.orangeTint,
      dark: brand.orangeDeep,
      contrastText: '#fff'
    },
    secondary: {
      main: neutral.wash,
      light: neutral.body,
      lightest: brand.orange,
      dark: neutral.muted,
      darkest: '#3EC6DD',
      contrastText: '#fff'
    },
    success: {
      main: '#16A34A',
      light: '#DCFCE7',
      lightest: neutral.wash,
      dark: brand.orangeTint,
      darkest: neutral.washDeep,
      contrastText: '#fff'
    },
    info: {
      main: '#2563EB',
      light: neutral.body,
      lightest: '#EFF6FF',
      dark: 'rgba(16, 24, 40, 0.08)',
      darkest: 'rgba(16, 24, 40, 0.10)',
      contrastText: '#fff'
    },
    error: {
      main: '#DC2626',
      light: '#B91C1C',
      lightest: brand.orange,
      contrastText: '#fff'
    },
    warning: {
      main: '#F59E0B',
      light: '#FCD34D',
      lightest: '#FEF3C7',
      dark: brand.orange,
      darkest: 'rgba(244, 102, 31, 0.18)',
      contrastText: '#fff'
    },
    grey: {
      50: '#F8FAFC',
      100: neutral.lineSoft,
      200: neutral.line,
      300: '#DDE1E9',
      400: '#C3C9D4',
      500: neutral.faint,
      600: neutral.muted,
      700: '#4B5563',
      800: '#1F2937',
      900: neutral.ink,
      A100: '#E5E8EE',
      A200: neutral.faint,
      A400: '#374151',
      A700: '#4B5563'
    },
    contrastThreshold: 3,
    tonalOffset: 0.2,
    text: {
      primary: neutral.ink,
      secondary: neutral.body,
      disabled: neutral.muted,
      hint: neutral.faint
    },
    divider: neutral.line,
    background: {
      paper: neutral.surface,
      default: neutral.wash,
      primary: '#F4F6F9'
    }
  },
  shadows: [
    'none',
    shadow.xs,
    shadow.sm,
    shadow.sm,
    shadow.md,
    shadow.md,
    shadow.md,
    shadow.md,
    shadow.lg,
    shadow.lg,
    shadow.lg,
    shadow.lg,
    shadow.lg,
    shadow.lg,
    shadow.lg,
    shadow.lg,
    shadow.lg,
    shadow.lg,
    shadow.lg,
    shadow.lg,
    shadow.lg,
    shadow.lg,
    shadow.lg,
    shadow.lg,
    shadow.lg
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: neutral.wash,
          color: neutral.ink
        }
      }
    },
    // Views wrap their content in <Container>; let it run the full width of
    // the content column and take horizontal padding from the layout instead.
    MuiContainer: {
      defaultProps: {
        maxWidth: false
      },
      styleOverrides: {
        root: {
          paddingLeft: 0,
          paddingRight: 0,
          '@media (min-width:600px)': {
            paddingLeft: 0,
            paddingRight: 0
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none'
        },
        rounded: {
          borderRadius: 16
        },
        elevation1: {
          boxShadow: shadow.sm
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: `1px solid ${neutral.line}`,
          boxShadow: shadow.sm
        }
      }
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true
      },
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: 'none',
          fontWeight: 600,
          paddingLeft: 18,
          paddingRight: 18,
          minHeight: 38
        },
        contained: {
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' }
        },
        containedPrimary: {
          backgroundColor: brand.orange,
          color: '#fff',
          '&:hover': { backgroundColor: brand.orangeHover }
        },
        outlined: {
          borderColor: neutral.line,
          color: neutral.body,
          backgroundColor: neutral.surface,
          '&:hover': {
            borderColor: neutral.faint,
            backgroundColor: neutral.wash
          }
        },
        sizeSmall: {
          minHeight: 30,
          fontSize: '0.8125rem',
          paddingLeft: 12,
          paddingRight: 12
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          color: neutral.muted,
          '&:hover': { backgroundColor: neutral.lineSoft }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: neutral.surface,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: neutral.line
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: neutral.faint
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: brand.orange,
            borderWidth: 1
          }
        },
        input: {
          color: neutral.ink,
          fontSize: '0.875rem',
          padding: '11px 14px'
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          color: neutral.ink,
          '&::placeholder': { color: neutral.faint, opacity: 1 }
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: neutral.muted,
          fontSize: '0.875rem',
          '&.Mui-focused': { color: brand.orange }
        }
      }
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: neutral.muted,
          '&.Mui-focused': { color: brand.orange }
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          color: neutral.ink,
          fontSize: '0.875rem'
        }
      }
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          border: `1px solid ${neutral.line}`,
          boxShadow: shadow.lg,
          marginTop: 6
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          color: neutral.ink,
          borderRadius: 8,
          margin: '2px 6px',
          minHeight: 36,
          '&:hover': { backgroundColor: neutral.wash },
          '&.Mui-selected': {
            backgroundColor: brand.orangeTint,
            color: brand.orange
          }
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          boxShadow: shadow.lg
        }
      }
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.0625rem',
          fontWeight: 700,
          color: neutral.ink
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
          fontSize: '0.75rem',
          height: 26
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${neutral.lineSoft}`,
          fontSize: '0.875rem',
          color: neutral.ink
        },
        head: {
          backgroundColor: neutral.wash,
          color: neutral.muted,
          fontWeight: 600,
          fontSize: '0.8125rem'
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: neutral.ink,
          borderRadius: 8,
          fontSize: '0.75rem',
          padding: '6px 10px'
        }
      }
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 3,
          borderRadius: 3,
          backgroundColor: brand.orange
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.875rem',
          color: neutral.muted,
          '&.Mui-selected': { color: brand.orange }
        }
      }
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': { color: brand.orange },
          '&.Mui-checked + .MuiSwitch-track': {
            backgroundColor: brand.orange
          }
        }
      }
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: neutral.faint,
          '&.Mui-checked': { color: brand.orange }
        }
      }
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: neutral.faint,
          '&.Mui-checked': { color: brand.orange }
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          fontSize: '0.875rem'
        }
      }
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: neutral.line }
      }
    },
    MuiLink: {
      styleOverrides: {
        root: { color: brand.orange }
      }
    }
  }
})

export { brand, neutral, shadow }
export default theme
